'use client'

import React, {ReactNode, useContext, useMemo, useRef, useState} from 'react'
import {ProductGridContext} from '@local/stores/contexts'
import {Button, Checkbox, Form, Locale, Pastil, SelectOptionType, Slideout, Tag} from '@local/ui/components'

import {Accordion as AccordionContainer, AccordionItem, AccordionItemButton, AccordionItemHeading, AccordionItemPanel, AccordionItemState} from '@gnu/react-accessible-accordion'
import {cx} from '@local/utils/cx'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {useDevice, useTranslate} from '@local/hooks'
import {ProductGridProps} from '@local/ui/blocks/productGrid/ProductGrid'

export interface ProductGridFilterProps {
	items: ProductGridProps['grid']
	regions: SelectOptionType[]
	dates: Array<{ startDate: Date, endDate: Date, date: string }>
	productCategories: Array<{ label: string; description: string; value: string; count: number, comingSoon: boolean }>
	maxGuests: number
	onSubmit: (items: ProductGridProps['grid']) => void;
	calendarNotice?: string
}

function getYearMonthFromDateString (dateString: string) {
	if (!dateString) return null
	const endDate = dateString.split(' - ')[1]
	if (!endDate) return null
	const formattedEndDate = new Date(endDate)
	const year = formattedEndDate.getFullYear()
	const month = formattedEndDate.getMonth() + 1
	return `${year}-${month}`
}

const generateMonthOptions = ():Array<SelectOptionType> => {
	const today = new Date()
	const months: Array<SelectOptionType> = []

	for (let i = 1; i <= 12; i++) {
		let year = today.getFullYear()
		let month = today.getMonth() + i
		if (month > 11) {
			year++
			month -= 12
		}
		const date = new Date(year, month, 1)
		const monthLabel = date.toLocaleString('default', {month: 'long'})
		months.push({label: `${monthLabel} ${year}`, value: `${year}-${(month + 1).toString()}`})
	}

	return months
}

type FilterStateType = { productCategory: string[]; date: string[]; region: string[] }

export const ProductGridFilter = ({items, onSubmit, productCategories, regions}: ProductGridFilterProps) => {
	const translate = useTranslate()
	const {isFilterPanelOpen, setFilterPanel} = useContext(ProductGridContext)
	const onClickClose = () => setFilterPanel(false)
	const device = useDevice()
	const isDesktop = device === 'desktop' ? true : undefined
	const slideshowRef = useRef<HTMLDivElement & { scrollToIndex(index: number) }>(null)
	const filterState = useRef<FilterStateType>({productCategory: [], date: [], region: []})
	const [filterItems, setFilterItems] = useState(items)
	const monthOptions = useMemo(() => generateMonthOptions(), [])

	const handleReset = () => {
		filterState.current = {productCategory: [], date: [], region: []}
		setFilterItems(items)
		if (slideshowRef.current) {
			slideshowRef.current.scrollToIndex(0)
		}
	}

	const onChange = () => {
		const {productCategory, date, region} = filterState.current
		const _filterItems = items.filter(({item}) => {
			if (productCategory && productCategory.length > 0 && !item.productCategories?.some(category => productCategory.includes(category.value))) {
				return false
			}

			if (date && date.length > 0) {
				const filterDates = Array.isArray(date) ? date : [date]
				const datePeriod = item.datePeriod
				const endMonth = getYearMonthFromDateString(datePeriod)
				if (!endMonth) return false
				if (!filterDates.includes(endMonth)) return false
			}

			// Filter by region
			if (region && region.length > 0 && item.region && !region.includes(item.region)) {
				return false
			}

			return true
		})
		setFilterItems(_filterItems)
	}

	const handleFilterChange = (e) => {
		const {name, value} = e.target
		const _filterState = {...filterState.current}
		const index = _filterState[name].indexOf(value)

		if (index > -1) {
			_filterState[name].splice(index, 1)
		} else if (e.target.checked) {
			_filterState[name].push(value)
		}

		filterState.current = _filterState
		onChange()
	}

	const onSubmitLocal = () => {
		onSubmit(filterItems)
		onClickClose()
	}

	return (
		<Slideout
			isOpen={isFilterPanelOpen}
			variant='right'
			className='bg-grey-4 text-white'
			containerClassName='[&>.slideout-container]:lg:!span-w-6-wide'
			onClickClose={onClickClose}>
			{/* Header */}
			<div className='flex items-center justify-between w-full pt-16 pb-10 lg:py-20'>
				<h3 className='w-fit uppercase'><Locale>Filter</Locale></h3>
				<Button
					small
					variant='icon'
					onClick={onClickClose}
					className='px-16 aspect-square'>
					<CloseSVG />
				</Button>
			</div>

			{/* Body */}
			<Form
				onSubmit={onSubmitLocal}
				autoReset={false}
				additionalReset={handleReset}
				className='flex flex-col overflow-y-auto h-full'>
				<AccordionContainer
					allowZeroExpanded
					allowMultipleExpanded
					className={cx('w-full [&_.accordion__panel-wrapper]:block mb-16')}>

					{/* category */}
					<AccordionFilterItem isExpanded={isDesktop} title={translate('Category')} className='grid grid-cols-2 gutter-gap-1 pb-20 lg:pb-24'>
						{productCategories.map((category, index) => (
							<div className='flex flex-col' key={`category-${index}`}>
								<Checkbox
									name='productCategory'
									id={`filter-category-${index}`}
									value={category.label}
									label={category.value}
									onChange={handleFilterChange}
									disabled={category.comingSoon}
									checkboxClassName='peer-checked:bg-grey-4'
									className='my-10 text-white capitalize'>
									<span className='relative'>{category.label}</span>
									{category.count ? <span>&nbsp;({category.count})</span> : <></>}
									{category.comingSoon && <Pastil variant='grey' className='inline-block ml-10'><Locale>Coming Soon</Locale></Pastil>}
								</Checkbox>
								{category?.description && <span className='text-9'>{category.description}</span>}
							</div>

						))}
					</AccordionFilterItem>
					<AccordionFilterItem isExpanded={isDesktop} title={translate('Dates')} className='grid gutter-gap-1 pb-20 lg:pb-24'>
						<div className='grid grid-cols-3 gap-10'>
							{monthOptions.map((option) => (
								<button
									key={option.value}
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()

										if (filterState.current.date.includes(option.value)) {
											filterState.current.date = filterState.current.date.filter(date => date !== option.value)
										} else {
											filterState.current.date.push(option.value)
										}

										onChange()
									}}>

									<span
										className={cx(
											'bg-transparent flex items-center justify-center h-48 text-12',
											'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
											'capitalize transition-colors duration-200 ease-quart-out hover:cursor-pointer',
											filterState.current.date.includes(option.value) ? 'border-white' : 'border-grey-2',
										)}>
										{option.label}
									</span>
								</button>
							))}
						</div>
					</AccordionFilterItem>

					{/* Region */}
					<AccordionFilterItem title={translate('Where')} className='pb-20 grid grid-cols-2 gutter-gap-1 lg:pb-24' isExpanded={isDesktop}>
						{regions.map((region, index) => (
							<Checkbox
								key={`region-${index}`}
								name='region'
								id={`filter-region-${index}`}
								value={region.value}
								label={region.label}
								checked={filterState.current.region.includes(region.value)}
								onChange={handleFilterChange}
								checkboxClassName='peer-checked:bg-grey-4'
								className='my-10 text-white capitalize [&>i]:peer-checked:bg-grey-4'>
								{region.label}
							</Checkbox>
						))}
					</AccordionFilterItem>
				</AccordionContainer>

				<div className='grid grid-cols-2 gutter-gap-1 pt-12 pb-20 mt-auto sticky bottom-0 bg-grey-4'>
					<Button type='reset' onClick={handleReset} variant='primary-transparent' small={!isDesktop}>
						<Locale>Clear All</Locale>
					</Button>
					<Button type='submit' variant='secondary' small={!isDesktop} disabled={!filterItems.length}>
						<Locale>Apply</Locale>
						<span className='ml-[1ch]'>({filterItems.length})</span>
					</Button>
				</div>
			</Form>
		</Slideout>
	)
}

interface AccordionFilterItemProps {
	title: string
	className?: string
	headingClassName?: string
	containerClassName?: string
	isExpanded?: boolean
	children: ReactNode
}

const AccordionFilterItem = ({title, children, className, headingClassName, containerClassName, isExpanded = undefined}: AccordionFilterItemProps) => (
	<AccordionItem
		key={`accordion-item-${title}`}
		uuid={`accordion-item-${title}`}
		dangerouslySetExpanded={isExpanded}
		className={cx('lg-max:border-b border-grey-1/30', containerClassName)}>
		<AccordionItemHeading
			aria-level={2}
			id={`accordion-item-${title}-heading`}>
			<AccordionItemButton
				className={cx('flex flex-row items-center justify-between py-20', headingClassName)}>
				<span className='text-11 uppercase tracking-wider'>
					<Locale>{title}</Locale>
				</span>
				<Tag className='bg-transparent underline underline-offset-4 lg:hidden' theme='dark'>
					<AccordionItemState>
						{({expanded}) => expanded
							? <Locale>Close</Locale>
							: <Locale>Select</Locale>}
					</AccordionItemState>
				</Tag>
			</AccordionItemButton>
		</AccordionItemHeading>
		<AccordionItemPanel animated className={className}>
			{children}
		</AccordionItemPanel>
	</AccordionItem>
)
